import React, { useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Loader2, RefreshCw } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Statuts disponibles pour les transferts (contract_runs)
const TRANSFER_STATUSES = [
  { value: 'planifiee',                  label: 'Planifié',               dotColor: 'bg-slate-400' },
  { value: 'en_cours_de_chargement',     label: 'En cours de chargement', dotColor: 'bg-blue-400' },
  { value: 'en_transit',                 label: 'En transit',             dotColor: 'bg-amber-400' },
  { value: 'en_cours_de_dechargement',   label: 'En cours de déchargement', dotColor: 'bg-purple-400' },
  { value: 'completed',                  label: 'Terminé',                dotColor: 'bg-emerald-400' },
  { value: 'cancelled',                  label: 'Annulé',                 dotColor: 'bg-red-400' },
];

/**
 * BulkStatusChangeModal
 * Modale de changement de statut en masse pour les transferts (contract_runs).
 *
 * Props:
 *  - open: bool
 *  - onClose: fn()
 *  - runIds: string[] — IDs des transferts sélectionnés
 *  - onSuccess: fn() — appelé après succès pour rafraîchir la liste
 */
const BulkStatusChangeModal = ({ open, onClose, runIds = [], onSuccess }) => {
  const [selectedStatus, setSelectedStatus] = useState('');
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    if (!selectedStatus) {
      toast({ title: 'Sélectionnez un statut', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/contract-runs/bulk-change-status`, {
        run_ids: runIds,
        new_status: selectedStatus,
      });

      const { success_count, failed_count } = res.data;

      toast({
        title: `✅ ${success_count} transfert(s) mis à jour`,
        description: failed_count > 0
          ? `${failed_count} transfert(s) n'ont pas pu être modifiés`
          : undefined,
      });

      setSelectedStatus('');
      onClose();
      if (onSuccess) onSuccess();
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err.response?.data?.detail || 'Erreur lors du changement de statut',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    setSelectedStatus('');
    onClose();
  };

  const selectedConfig = TRANSFER_STATUSES.find(s => s.value === selectedStatus);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-blue-600" />
            Changer le statut
          </DialogTitle>
          <DialogDescription>
            Appliquer un nouveau statut à tous les transferts sélectionnés en une seule action.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Résumé de la sélection */}
          <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <Badge className="bg-blue-600 text-white">
              {runIds.length} transfert{runIds.length > 1 ? 's' : ''} sélectionné{runIds.length > 1 ? 's' : ''}
            </Badge>
            <span className="text-sm text-blue-700">seront mis à jour</span>
          </div>

          {/* Sélection du statut */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Nouveau statut
            </label>
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger>
                <SelectValue placeholder="Choisir un statut..." />
              </SelectTrigger>
              <SelectContent>
                {TRANSFER_STATUSES.map(s => (
                  <SelectItem key={s.value} value={s.value}>
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${s.dotColor} inline-block`} />
                      {s.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Aperçu du statut choisi */}
          {selectedConfig && (
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border text-sm text-gray-700">
              <span className={`w-2.5 h-2.5 rounded-full ${selectedConfig.dotColor}`} />
              Tous les transferts sélectionnés passeront à&nbsp;
              <strong>{selectedConfig.label}</strong>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Annuler
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedStatus || loading}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Mise à jour...</>
            ) : (
              <>Confirmer ({runIds.length})</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkStatusChangeModal;
